import { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export interface Attempt {
  id: string;
  personName: string;
  attemptType: string;
  note: string;
  createdAt: string;
}

interface AttemptLogProps {
  attempts: Attempt[];
  isLoading: boolean;
  onLogAttempt: (note: string) => void;
  isSubmitting: boolean;
}

export function AttemptLog({ attempts, isLoading, onLogAttempt, isSubmitting }: AttemptLogProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [note, setNote] = useState('');

  const handleSubmit = () => {
    if (!note.trim()) return;
    onLogAttempt(note.trim());
    setNote('');
    setIsAdding(false);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground transition-colors"
        >
          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {attempts.length} attempt{attempts.length !== 1 ? 's' : ''}
        </button>
        <button
          onClick={() => { setIsAdding(true); setIsExpanded(true); }}
          className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover transition-colors"
        >
          <Plus className="h-3 w-3" />
          Log attempt
        </button>
      </div>

      {isExpanded && (
        <div className="mt-2 ml-4 space-y-2">
          {isAdding && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g., emailed vendor, called John..."
                className="flex-1 px-2 py-1 border border-border rounded text-xs bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                  if (e.key === 'Escape') { setIsAdding(false); setNote(''); }
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !note.trim()}
                className="px-2 py-1 text-xs font-medium rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </button>
              <button
                onClick={() => { setIsAdding(false); setNote(''); }}
                className="px-2 py-1 text-xs text-foreground-muted hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-foreground-muted">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading...
            </div>
          ) : attempts.length === 0 ? (
            <p className="text-xs text-foreground-muted">No attempts logged yet.</p>
          ) : (
            attempts.map((attempt) => (
              <div key={attempt.id} className="text-xs border-l-2 border-border pl-3 py-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{attempt.personName}</span>
                  <span className="text-foreground-muted">
                    {formatDistanceToNow(new Date(attempt.createdAt), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-foreground-muted mt-0.5">{attempt.note}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
